import { useCanvasStore } from '../store/canvas-store';
import { usePanelStore } from './panel/panel-store';
import { encodeDoc, decodeDoc, SchemaError } from '../io/serialize';
import { frameToHtmlDoc } from '../io/export-html';
import { downloadBlob, pickFile } from '../io/download';
import { emptyState } from '../store/commands';
import { boundsOf } from '../store/selectors';
import { isPanelOwned } from '../store/types';
import { spawnTsxComponent } from './spawnTsx';
import {
  tidyCanvas, tidySelection, alignSelection, distributeSelection,
} from './selectionActions';
import { onboardingDoc } from '../templates/onboarding';
import { useAuth } from '../auth/useAuth';
import { ensurePanelChat } from './panel/panelFrames';
import { useStockPhotosStore } from './stockPhotos/stockPhotos-store';
import { newConversation } from '../agent/chatTurn';
import { exportBundle, importBundle, isBundle } from '../assets/bundle';
import { collectAssetIds } from '../assets/resolveAssets';
import { assetStore } from '../assets/assetStore';
import { useProjectsScreen } from '../projects/projectsScreen-store';
import type { Command } from './CommandPalette';

async function importDoc(): Promise<void> {
  const file = await pickFile('.json,.cfbundle.json');
  if (!file) return;
  const ok = window.confirm(
    `Import "${file.name}"?\n\nFrames will render as live HTML. Only import canvases from sources you trust.`,
  );
  if (!ok) return;
  try {
    const text = await file.text();
    const doc = isBundle(text) ? await importBundle(text) : decodeDoc(text);
    useCanvasStore.getState().load({ ...emptyState(), doc });
  } catch (err) {
    const msg = err instanceof SchemaError ? err.message : String(err);
    window.alert(`Import failed: ${msg}`);
  }
}

function exportDoc(): void {
  const doc = useCanvasStore.getState().doc;
  if (
    collectAssetIds(doc).length > 0 &&
    !window.confirm(
      'This canvas references stored media. Plain JSON export will NOT include it — links will break on another machine. Export the bundle (.cfbundle.json) instead to keep media. Export plain JSON anyway?',
    )
  ) {
    return;
  }
  downloadBlob('codeframe-canvas.json', encodeDoc(doc), 'application/json');
}

async function exportBundleCmd(): Promise<void> {
  const doc = useCanvasStore.getState().doc;
  const text = await exportBundle(doc);
  downloadBlob('codeframe-canvas.cfbundle.json', text, 'application/json');
}

async function cleanUnusedAssets(): Promise<void> {
  const doc = useCanvasStore.getState().doc;
  const referenced = new Set(collectAssetIds(doc));
  const stored = await assetStore.keys();
  const orphans = stored.filter((id) => !referenced.has(id));
  if (orphans.length === 0) {
    window.alert('No unused assets to clean.');
    return;
  }
  if (!window.confirm(`Delete ${orphans.length} unused asset(s) from local storage? This cannot be undone.`)) return;
  for (const id of orphans) await assetStore.delete(id);
  window.alert(`Removed ${orphans.length} unused asset(s).`);
}

function exportSelected(): void {
  const state = useCanvasStore.getState();
  const f = state.doc.frames.find((x) => x.id === state.selection[0]);
  if (!f) return;
  const byId = new Map(state.doc.frames.map((x) => [x.id, x] as const));
  downloadBlob(
    `${f.title.replace(/[^a-z0-9._-]/gi, '_') || 'frame'}.html`,
    frameToHtmlDoc(f, byId),
    'text/html',
  );
}

function loadOnboarding(): void {
  const state = useCanvasStore.getState();
  if (
    state.doc.frames.length > 0 &&
    !window.confirm('Replace the current canvas with the onboarding tour? Your current work will be cleared (Undo still works).')
  ) {
    return;
  }
  state.load({ ...emptyState(), doc: onboardingDoc() });
}

/**
 * App-level commands for the ⌘P palette. Migrated from the ActionChip ⋯ menu.
 * Reactive on the bits that appear in titles (unified / annotations / mode) so
 * checkmarks stay current; one-shot actions read fresh state via getState().
 */
export function useAppCommands(openBookmarklet: () => void): Command[] {
  const annotationsVisible = useCanvasStore((s) => s.annotationsVisible);
  const themes = useCanvasStore((s) => s.doc.themes) ?? [];
  const activeThemeId = useCanvasStore((s) => s.doc.activeThemeId);
  const selCount = useCanvasStore((s) => s.selection.length);
  const authed = useAuth((s) => s.status === 'authenticated');

  const commands: Command[] = [];

  commands.push(
    { id: 'open-projects', title: 'Projects…', run: () => useProjectsScreen.getState().openScreen() },
    { id: 'new-tsx', title: 'New TSX component', run: () => { spawnTsxComponent(); } },
    { id: 'stock-photos', title: 'Stock photos: search & place…', run: () => useStockPhotosStore.getState().setOpen(true) },
    {
      id: 'add-lottie',
      title: 'Add Lottie animation…',
      // Opens the ⌘K chat bubble pre-filled — the user finishes the sentence.
      run: () => window.dispatchEvent(new CustomEvent('codeframe:prefill-chat', { detail: 'Add a Lottie animation of ' })),
    },
    { id: 'zoom-fit', title: 'Zoom to fit', run: () => {
      const st = useCanvasStore.getState();
      const visible = st.doc.frames.filter((f) => !isPanelOwned(f) && f.kind !== 'note');
      const b = boundsOf(visible);
      if (b) st.dispatch({ t: 'viewport.focus', bounds: { ...b, w: Math.max(b.w, 1), h: Math.max(b.h, 1) } });
    } },
    { id: 'tidy-canvas', title: 'Arrange: tidy canvas into a grid', run: tidyCanvas },
  );

  // Align / distribute only make sense with a multi-selection.
  if (selCount >= 2) {
    commands.push(
      { id: 'tidy-selection', title: 'Arrange: tidy selection into a grid', run: tidySelection },
      { id: 'align-left', title: 'Align: left edges', run: () => alignSelection('left') },
      { id: 'align-hcenter', title: 'Align: horizontal centers', run: () => alignSelection('hcenter') },
      { id: 'align-right', title: 'Align: right edges', run: () => alignSelection('right') },
      { id: 'align-top', title: 'Align: top edges', run: () => alignSelection('top') },
      { id: 'align-vcenter', title: 'Align: vertical centers', run: () => alignSelection('vcenter') },
      { id: 'align-bottom', title: 'Align: bottom edges', run: () => alignSelection('bottom') },
    );
  }
  if (selCount >= 3) {
    commands.push(
      { id: 'distribute-h', title: 'Distribute: evenly horizontal', run: () => distributeSelection('h') },
      { id: 'distribute-v', title: 'Distribute: evenly vertical', run: () => distributeSelection('v') },
    );
  }

  commands.push(
    { id: 'export-json', title: 'Export canvas as JSON', run: exportDoc },
    { id: 'export-bundle', title: 'Export canvas + assets (bundle)', run: () => { void exportBundleCmd(); } },
    { id: 'import-json', title: 'Import canvas from JSON or bundle', run: () => { void importDoc(); } },
    { id: 'export-selected', title: 'Export selected frame as HTML', run: exportSelected },
    { id: 'publish', title: 'Publish selected frames…', run: () => usePanelStore.getState().setPublishOpen(true) },
    { id: 'clean-assets', title: 'Clean unused assets', run: () => { void cleanUnusedAssets(); } },
    { id: 'toggle-annotations', title: annotationsVisible ? 'Hide annotations' : 'Show annotations', run: () => useCanvasStore.getState().toggleAnnotations() },
    { id: 'load-onboarding', title: 'Load onboarding tour', run: loadOnboarding },
    {
      id: 'generate-theme',
      title: '✨ Generate a theme with the agent…',
      // Opens the ⌘K chat bubble in theme-intent mode (auto-grow textarea);
      // CmdK wraps the submitted text into the theme-generation prompt.
      run: () => window.dispatchEvent(new CustomEvent('codeframe:generate-theme')),
    },
    ...themes.map((t) => ({
      id: `active-theme-${t.id}`,
      title: `Active theme: ${t.name}${activeThemeId === t.id ? ' ✓' : ''}`,
      run: () => useCanvasStore.getState().dispatch({ t: 'doc.setActiveTheme', themeId: t.id }),
    })),
    ...(themes.length ? [{ id: 'active-theme-none', title: `Active theme: none${!activeThemeId ? ' ✓' : ''}`, run: () => useCanvasStore.getState().dispatch({ t: 'doc.setActiveTheme', themeId: undefined }) }] : []),
    {
      id: 'clear-canvas',
      title: 'Clear canvas',
      run: () => {
        const s = useCanvasStore.getState();
        if (s.doc.frames.length > 0 && !window.confirm('Clear the whole canvas? Undo still works.')) return;
        s.dispatch({ t: 'doc.clear' });
      },
    },
    { id: 'capture-bookmarklet', title: 'Get capture bookmarklet', run: openBookmarklet },
    { id: 'save-checkpoint', title: 'Save checkpoint…', run: () => {
      const label = window.prompt('Checkpoint label?');
      if (label === null) return;
      void import('../history/historyStore').then(({ historyStore }) =>
        historyStore.putCheckpoint({ kind: 'manual', label: label || new Date().toLocaleString(), doc: useCanvasStore.getState().doc })
          .then(() => historyStore.pruneCheckpoints(20, 50)));
    } },
    { id: 'open-checkpoints', title: 'Checkpoints…', run: () => usePanelStore.getState().setCheckpointsOpen(true) },
    { id: 'new-conversation', title: 'New conversation', run: () => {
      const st = useCanvasStore.getState();
      const id = ensurePanelChat({ frames: st.doc.frames, dispatch: st.dispatch });
      newConversation(id);
      usePanelStore.getState().setOpen(true);
    } },
    { id: 'connect-agent', title: 'Connect agent…', run: () => usePanelStore.getState().setConnectAgentOpen(true) },
  );

  // Sign out — only when an actual Cognito session is active (prod).
  if (authed) {
    commands.push({ id: 'sign-out', title: 'Sign out', run: () => useAuth.getState().signOut() });
  }

  return commands;
}
